import { useMemo, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { useProducts } from "../lib/products";
import Reveal from "../components/Reveal";
import ProductCard from "../components/ProductCard";

const SORTS = [
  { value: "featured", label: "SORT: FEATURED" },
  { value: "price-asc", label: "PRICE: LOW TO HIGH" },
  { value: "price-desc", label: "PRICE: HIGH TO LOW" },
];

export default function Shop() {
  const { products, state, retry } = useProducts();
  const [q, setQ] = useState("");
  const [sizeFilter, setSizeFilter] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);
  const [sort, setSort] = useState("featured");

  const allSizes = useMemo(() => [...new Set(products.flatMap((p) => p.sizes || []))], [products]);
  const allColors = useMemo(() => {
    const seen = new Set();
    const out = [];
    products.forEach((p) => (p.colors || []).forEach((c) => {
      if (!seen.has(c.name)) { seen.add(c.name); out.push(c); }
    }));
    return out;
  }, [products]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = products.filter((p) => {
      if (sizeFilter.length && !(p.sizes || []).some((s) => sizeFilter.includes(s))) return false;
      if (colorFilter.length && !(p.colors || []).some((c) => colorFilter.includes(c.name))) return false;
      if (term) {
        const hay = [p.title, p.code, p.tag, p.gsm, p.fit, p.description,
          ...(p.colors || []).map((c) => c.name), ...(p.sizes || [])].join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    if (sort === "price-asc") return [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") return [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, q, sizeFilter, colorFilter, sort]);

  const toggleSize = (s) => setSizeFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const toggleColor = (n) => setColorFilter((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  const hasFilters = Boolean(q.trim() || sizeFilter.length || colorFilter.length || sort !== "featured");
  const clearAll = () => { setQ(""); setSizeFilter([]); setColorFilter([]); setSort("featured"); };

  return (
    <main className="px-5 md:px-10 pt-28 pb-24 max-w-[1600px] mx-auto">
      <Reveal className="mb-10">
        <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-3">DROP 001 — FULL GRID</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl">
          THE CATALOGUE <span className="font-mono text-sm text-mute align-middle ml-3">{String(products.length).padStart(2, "0")} UNITS</span>
        </h1>
      </Reveal>

      <Reveal className="mb-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-3 border border-white/15 bg-surface px-4 py-3 focus-within:border-acid transition-colors duration-300">
            <Search size={15} className="text-dim shrink-0" />
            <input
              data-testid="shop-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SEARCH NAME, COLOR, FIT…"
              className="w-full bg-transparent font-mono text-[11px] tracking-[0.2em] uppercase text-ink placeholder:text-mute outline-none"
            />
            {q && (
              <button data-testid="shop-search-clear-btn" onClick={() => setQ("")} aria-label="Clear search" className="text-dim hover:text-white transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="relative self-start sm:self-auto">
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort products"
              className="appearance-none w-full sm:w-auto border border-white/15 bg-surface font-mono text-[11px] tracking-[0.2em] text-dim pl-4 pr-10 py-3 outline-none hover:border-acid hover:text-acid transition-colors duration-300 cursor-pointer"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value} className="bg-[#0E1015]">{s.label}</option>)}
            </select>
            <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-mute" />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.25em] text-mute mr-1">SIZE</span>
            {allSizes.map((s) => (
              <button
                key={s}
                data-testid={`shop-size-chip-${s.toLowerCase()}`}
                onClick={() => toggleSize(s)}
                className={`font-mono text-[11px] px-3 py-1.5 border transition-colors duration-300 ${sizeFilter.includes(s) ? "bg-volt text-void border-volt font-bold" : "border-white/15 text-dim hover:border-acid hover:text-acid"}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <span className="font-mono text-[10px] tracking-[0.25em] text-mute mr-1">COLOR</span>
            {allColors.map((c) => (
              <button
                key={c.name}
                data-testid={`shop-color-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggleColor(c.name)}
                className={`flex items-center gap-2 font-mono text-[11px] px-3 py-1.5 border transition-colors duration-300 ${colorFilter.includes(c.name) ? "border-acid text-acid" : "border-white/15 text-dim hover:border-acid hover:text-acid"}`}
              >
                <span className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ backgroundColor: c.hex }} />
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p data-testid="shop-results-count" className="font-mono text-[11px] tracking-widest text-mute">
            SHOWING {String(filtered.length).padStart(2, "0")} / {String(products.length).padStart(2, "0")} UNITS
          </p>
          {hasFilters && (
            <button data-testid="shop-clear-filters-btn" onClick={clearAll} className="font-mono text-[11px] tracking-widest text-volt hover:text-acid transition-colors duration-300 underline underline-offset-4">
              CLEAR ALL
            </button>
          )}
        </div>
      </Reveal>

      {state === "error" ? (
        <div data-testid="shop-load-error" className="border border-white/[0.08] py-24 text-center">
          <p className="font-display text-xl md:text-2xl font-bold uppercase">COULDN'T LOAD THE DROP</p>
          <p className="font-mono text-[11px] tracking-widest text-mute mt-3">CHECK YOUR CONNECTION AND TRY AGAIN</p>
          <button data-testid="shop-retry-btn" onClick={retry} className="mt-6 font-mono text-[11px] tracking-[0.25em] border border-white/15 px-6 py-3 text-dim hover:border-acid hover:text-acid transition-colors duration-300">
            RETRY
          </button>
        </div>
      ) : state === "loading" || products.length === 0 ? (
        <p className="font-mono text-[11px] tracking-widest text-mute py-24 text-center">LOADING THE DROP…</p>
      ) : filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p, i) => (
            <Reveal key={p._id} delay={(i % 3) * 0.07} className="h-full">
              <div className="h-full [&>a]:h-full">
                <ProductCard product={p} />
              </div>
            </Reveal>
          ))}
        </div>
      ) : (
        <div data-testid="shop-empty-state" className="border border-white/[0.08] py-24 text-center">
          <p className="font-display text-xl md:text-2xl font-bold uppercase">NO UNITS MATCH YOUR SEARCH</p>
          <p className="font-mono text-[11px] tracking-widest text-mute mt-3">TRY A DIFFERENT COLOR, SIZE OR KEYWORD</p>
          <button data-testid="shop-empty-clear-btn" onClick={clearAll} className="mt-6 font-mono text-[11px] tracking-[0.25em] border border-white/15 px-6 py-3 text-dim hover:border-acid hover:text-acid transition-colors duration-300">
            CLEAR FILTERS
          </button>
        </div>
      )}
    </main>
  );
}
